import { useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { type ReactNode, useState } from "react";
import {
  ActivityIndicator,
  Linking,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";

import {
  DISCLAIMER,
  contrastBullet,
  formatAud,
  minimumTrapBullet,
  payoffHeadline,
  sourceBullet,
} from "@aucash/rules/credit-card-payoff";

import { AppBar, Button, Card, Chip, Coin, Eyebrow } from "../../src/components/brand";
import { Field, MoneyInput, Segmented } from "../../src/components/controls";
import { SHORT_DISCLAIMER } from "../../src/disclaimer";
import { trpc } from "../../src/trpc";
import { colors, fonts, radius, space, tabular, webNoOutline } from "../../src/theme";

// credit-card-payoff — the minimum-repayment "debt trap" calculator, surfaced
// stateless-v1 (PLAN arch decision #1). One screen; the result renders INLINE
// (no save-by-id, no auth gate, no persistence). Each compute is a stateless
// `calc.creditCardPayoff.compute` query — input -> output, no round-trip to a
// stored row.
//
// REGULATORY (binding — NCCP). A credit card IS a credit product; this tool is
// INFORMATION about the user's OWN existing debt (fine) but MUST NOT hand off to
// any credit product (balance-transfer card / personal loan / debt
// consolidation) from a personalised result. The trap is presented as
// ARITHMETIC — "paying the minimum, this balance takes N years and $X interest"
// — never "consolidate"/"balance transfer"/"you should". Every string rendered
// here comes verbatim from the rules layer's copy helpers, whose output is run
// through the `assertNoCreditHandoff` guard, so the kill-line holds at the UI
// surface too. The form deliberately offers NO credit-product CTA.

// The schedule needs a deterministic start date (never Date.now() in the rules
// layer). Stable, passed-in date so the payoff date is reproducible.
const START_DATE = "2026-07-01";

/** Which path the result headlines. Both paths are always computed (the trap is
 * the contrast); this only chooses the focus of the headline. */
type Strategy = "fixed" | "minimum";

const STRATEGY_OPTIONS: ReadonlyArray<{ value: Strategy; label: string }> = [
  { value: "fixed", label: "A fixed amount" },
  { value: "minimum", label: "Only the minimum" },
];

type ComputeResult = Awaited<
  ReturnType<typeof trpc.calc.creditCardPayoff.compute.query>
>;

export default function CreditCardPayoffIndex(): JSX.Element {
  const router = useRouter();

  const [strategy, setStrategy] = useState<Strategy>("fixed");
  const [balance, setBalance] = useState("");
  const [aprPct, setAprPct] = useState("");
  const [monthlyPayment, setMonthlyPayment] = useState("");

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<ComputeResult | null>(null);

  const num = (s: string): number => Number(s || "0");

  const onStrategyChange = (next: Strategy): void => {
    setStrategy(next);
    setResult(null);
    setSubmitError(null);
    setErrors({});
  };

  const validate = (): Record<string, string> => {
    const next: Record<string, string> = {};
    if (balance.trim() === "")
      next.balance = "Enter the balance currently on the card.";
    if (aprPct.trim() === "" || Number(aprPct) <= 0)
      next.apr = "Enter the card's purchase interest rate (APR).";
    if (monthlyPayment.trim() === "" || Number(monthlyPayment) <= 0)
      next.payment =
        "Enter the fixed amount you'd pay each month — we'll contrast it with paying only the minimum.";
    return next;
  };

  const onSubmit = (): void => {
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length > 0 || submitting) return;

    setSubmitting(true);
    setSubmitError(null);
    setResult(null);
    trpc.calc.creditCardPayoff.compute
      .query({
        input: {
          balance: num(balance),
          // APR entered as a percentage; the rules layer takes a fraction.
          annualRate: num(aprPct) / 100,
          fixedMonthlyPayment: num(monthlyPayment),
          startDate: START_DATE,
        },
      })
      .then((res) => setResult(res))
      .catch((err: unknown) =>
        setSubmitError(
          err instanceof Error ? err.message : "We couldn't run the calculator.",
        ),
      )
      .finally(() => setSubmitting(false));
  };

  return (
    <View style={styles.root}>
      <StatusBar style="dark" />
      <AppBar pill="Credit card payoff" />
      <ScrollView contentContainerStyle={styles.scroll}>
        <View style={styles.stage}>
          <View style={styles.navRow}>
            <Pressable onPress={() => router.push("/")} accessibilityRole="button">
              <Text style={styles.navLink}>← Calculators</Text>
            </Pressable>
          </View>

          <View style={styles.header}>
            <Coin size={56} />
            <Eyebrow>Credit card · minimum-repayment trap</Eyebrow>
            <Text style={styles.title}>How long until the card is paid off?</Text>
            <Text style={styles.subtitle}>
              At your own balance and interest rate, see how long the balance
              takes to clear and how much interest it costs — and the contrast
              between paying a fixed amount and paying only the minimum, using
              the minimum-repayment rule described by ASIC MoneySmart.
            </Text>
            <Text style={styles.shortDisclaimer}>{SHORT_DISCLAIMER}</Text>
          </View>

          <Field
            label="Current balance on the card"
            help="The amount currently owing — we don't store it."
            error={errors.balance}
          >
            <MoneyInput
              value={balance}
              onChange={setBalance}
              testID="input-balance"
              accessibilityLabel="Current balance on the card"
            />
          </Field>

          <Field
            label="Purchase interest rate (APR)"
            help="The annual purchase rate on your statement, e.g. 20.99."
            error={errors.apr}
          >
            <View style={styles.pctWrap}>
              <TextInput
                value={aprPct}
                onChangeText={(t) => setAprPct(t.replace(/[^0-9.]/g, ""))}
                keyboardType="decimal-pad"
                placeholder="20.99"
                placeholderTextColor={colors.muted}
                testID="input-apr"
                accessibilityLabel="Purchase interest rate, annual percentage rate, in percent"
                style={styles.pctInput}
              />
              <Text style={styles.pctSuffix}>% p.a.</Text>
            </View>
          </Field>

          <Field
            label="What could you pay each month?"
            help="The fixed amount you'd commit to monthly — we contrast it with paying only the minimum (the greater of 2% of the balance or $20, the ASIC MoneySmart rule)."
            error={errors.payment}
          >
            <MoneyInput
              value={monthlyPayment}
              onChange={setMonthlyPayment}
              testID="input-monthly-payment"
              accessibilityLabel="Fixed amount you could pay each month"
            />
          </Field>

          <Field label="Which path do you want to focus on?">
            <Segmented<Strategy>
              options={STRATEGY_OPTIONS}
              value={strategy}
              onChange={onStrategyChange}
              testIDPrefix="strategy"
            />
          </Field>

          <Button
            label={submitting ? "Calculating…" : "See the payoff →"}
            onPress={onSubmit}
            disabled={submitting}
            full
            testID="credit-card-payoff-submit"
          />
          {submitError !== null ? (
            <Text style={styles.submitError}>{submitError}</Text>
          ) : null}

          {submitting && result === null ? (
            <View style={styles.loading}>
              <ActivityIndicator color={colors.honey} />
            </View>
          ) : null}

          {result !== null ? (
            <ResultBlock result={result} strategy={strategy} />
          ) : null}
        </View>
      </ScrollView>
    </View>
  );
}

/** Inline result — stateless, rendered straight under the form (no save-by-id).
 * The full disclaimer (rules-layer DISCLAIMER) sits at the TOP, before any
 * number (validation §3.2 placement rule). All copy comes from the rules-layer
 * helpers, whose output passed `assertNoCreditHandoff` — no credit-product CTA,
 * no "consolidate"/"you should"/"save $X", arithmetic only. */
function ResultBlock({
  result,
  strategy,
}: {
  result: ComputeResult;
  strategy: Strategy;
}): JSX.Element {
  const { output, ruleVersion, sourcesVerifiedAt, sources } = result;
  const focus = strategy === "minimum" ? output.minimumOnly : output.fixed;

  // The headline of the path the user chose to focus on. `payoffHeadline`
  // already states the never-clears case ("does not cover the monthly interest,
  // so the balance never reduces") in plain arithmetic.
  const headline = payoffHeadline(focus);
  // The minimum-trap bullet + the fixed-vs-minimum contrast — the core framing.
  const trapBullet = minimumTrapBullet(output);
  const contrast = contrastBullet(output);

  return (
    <View style={styles.resultStage}>
      {/* §3.2: full disclaimer at the top, before any number. */}
      <View style={styles.disclaimerBlock}>
        <Text style={styles.disclaimerText}>{DISCLAIMER}</Text>
      </View>

      <View style={styles.resHead}>
        <Eyebrow>Your payoff · estimate</Eyebrow>
        <Chip label="Estimate" tone="est" />
      </View>

      <Card style={{ gap: space.sm }}>
        <Text style={styles.lead}>{headline}</Text>
      </Card>

      {/* Key figures grid — months-to-clear + total interest, the focus path. */}
      <View style={styles.grid}>
        <Stat
          k="Time to clear"
          v={
            focus.clears && focus.monthsToClear !== null
              ? formatMonths(focus.monthsToClear)
              : "Never clears"
          }
        />
        <Stat k="Total interest" v={formatAud(focus.totalInterestPaid)} />
      </View>

      {/* The minimum-repayment trap contrast — arithmetic, never a nudge. */}
      <Section title="Paying only the minimum">
        <Text style={styles.bodyText}>{trapBullet}</Text>
        {contrast !== null ? (
          <Text style={styles.bodyText}>{contrast}</Text>
        ) : null}
        {output.minimumInterestExceedsPrincipal ? (
          <Text style={styles.bodyText}>
            On the minimum-only path, the interest paid is more than the{" "}
            {formatAud(output.balance)} originally owed.
          </Text>
        ) : null}
      </Section>

      <Section title="Where these numbers come from">
        <Text style={styles.bodyText}>
          {sourceBullet(ruleVersion, sourcesVerifiedAt)}
        </Text>
        {Object.entries(sources).map(([key, url]) => (
          <SourceLink key={key} label={url} url={url} />
        ))}
        <Text style={styles.verified}>
          Benchmarks last verified {sourcesVerifiedAt} · set {ruleVersion}
        </Text>
      </Section>
    </View>
  );
}

/** Whole months as a human "N years M months" (mirrors the rules-layer
 * formatDuration; kept local so the grid stat can render it standalone). */
function formatMonths(months: number): string {
  const years = Math.floor(months / 12);
  const rem = months % 12;
  const parts: string[] = [];
  if (years > 0) parts.push(`${years}y`);
  if (rem > 0) parts.push(`${rem}mo`);
  return parts.length > 0 ? parts.join(" ") : "0mo";
}

function Stat({ k, v }: { k: string; v: string }): JSX.Element {
  return (
    <View style={styles.stat}>
      <Text style={styles.statK}>{k}</Text>
      <Text style={styles.statV}>{v}</Text>
    </View>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {children}
    </View>
  );
}

function SourceLink({
  label,
  url,
}: {
  label: string;
  url: string | undefined;
}): JSX.Element | null {
  if (url === undefined) return null;
  return (
    <Pressable
      onPress={() => void Linking.openURL(url)}
      accessibilityRole="link"
      style={styles.sourceLink}
    >
      <Text style={styles.sourceLinkText}>{label}</Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.cream },
  scroll: { padding: space.xl, paddingBottom: space.xxl, alignItems: "center" },
  stage: { width: "100%", maxWidth: 560, gap: space.xl },
  navRow: { flexDirection: "row", justifyContent: "space-between" },
  navLink: { fontFamily: fonts.bodySemibold, fontSize: 14, color: colors.honeyDeep },
  header: { gap: space.sm },
  title: {
    fontFamily: fonts.display,
    fontSize: 30,
    lineHeight: 34,
    letterSpacing: -1,
    color: colors.ink,
    marginTop: space.xs,
  },
  subtitle: { fontFamily: fonts.body, fontSize: 16, lineHeight: 23, color: colors.inkSoft },
  shortDisclaimer: {
    fontFamily: fonts.bodySemibold,
    fontSize: 12,
    color: colors.muted,
    marginTop: space.xs,
  },

  pctWrap: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: colors.card,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    paddingHorizontal: space.lg,
  },
  pctInput: {
    flex: 1,
    fontFamily: fonts.bodySemibold,
    fontSize: 18,
    color: colors.ink,
    paddingVertical: space.md,
    ...tabular,
    ...webNoOutline,
  },
  pctSuffix: { fontFamily: fonts.bodyMedium, fontSize: 14, color: colors.muted },

  submitError: {
    fontFamily: fonts.bodyMedium,
    fontSize: 14,
    color: colors.danger,
    textAlign: "center",
  },
  loading: { paddingVertical: space.xl, alignItems: "center" },

  resultStage: { gap: space.xl, marginTop: space.sm },
  disclaimerBlock: {
    backgroundColor: colors.sand,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.lg,
  },
  disclaimerText: {
    fontFamily: fonts.body,
    fontSize: 12,
    lineHeight: 18,
    color: colors.inkSoft,
  },
  resHead: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  lead: {
    fontFamily: fonts.displayHeavy,
    fontSize: 24,
    lineHeight: 30,
    letterSpacing: -0.5,
    color: colors.ink,
    ...tabular,
  },
  bodyText: { fontFamily: fonts.body, fontSize: 14.5, lineHeight: 22, color: colors.inkSoft },

  grid: { flexDirection: "row", gap: space.md },
  stat: {
    flex: 1,
    backgroundColor: colors.sand,
    borderRadius: radius.md,
    padding: space.lg,
    gap: space.xs,
  },
  statK: {
    fontFamily: fonts.bodySemibold,
    fontSize: 11,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    color: colors.muted,
  },
  statV: {
    fontFamily: fonts.displayHeavy,
    fontSize: 22,
    color: colors.ink,
    ...tabular,
  },

  section: { gap: space.md },
  sectionTitle: {
    fontFamily: fonts.display,
    fontSize: 20,
    letterSpacing: -0.4,
    color: colors.ink,
  },
  sourceLink: {
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.md,
    backgroundColor: colors.card,
  },
  sourceLinkText: { fontFamily: fonts.bodySemibold, fontSize: 13, color: colors.oceanDeep },
  verified: { fontFamily: fonts.body, fontSize: 12, color: colors.muted },
});
